import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, MapPin, Phone, Star } from "lucide-react";
import { SERVICES, SERVICE_AREAS, COMPANY, TESTIMONIALS } from "../data/site";
import { useReveal } from "../hooks/use-reveal";
import { useSeo } from "../hooks/use-seo";
import { localBusinessJsonLd, breadcrumbJsonLd, SITE_BASE_URL } from "../lib/jsonld";
import { ContactForm } from "../components/sections/ContactForm";

export default function AreaDetail() {
    useReveal();
    const { slug } = useParams();
    const area = SERVICE_AREAS.find((a) => a.slug === slug);

    useSeo(
        area
            ? {
                  title: `Insulation Services in ${area.city}, ${area.state} | HAZ Insulation`,
                  description: `Spray foam, attic, crawl space, dense-pack and air sealing for ${area.city}, ${area.state} (${area.zip}) homeowners. BPI-certified crew, NICOR & ComEd rebate partner — up to ${COMPANY.maxRebate} back. Free quote in 48 hours.`,
                  canonical: `${SITE_BASE_URL}/areas/${area.slug}`,
                  jsonLd: [
                      localBusinessJsonLd({
                          url: `${SITE_BASE_URL}/areas/${area.slug}`,
                          withRating: true,
                      }),
                      breadcrumbJsonLd([
                          { name: "Home", url: "/" },
                          { name: "Service Areas", url: "/areas" },
                          { name: `${area.city}, ${area.state}`, url: `/areas/${area.slug}` },
                      ]),
                  ],
              }
            : undefined
    );

    if (!area) return <Navigate to="/" replace />;

    const localTestimonials = TESTIMONIALS.filter(
        (t) =>
            t.location?.toLowerCase().includes(area.city.toLowerCase()) ||
            t.location?.toLowerCase().includes(area.state.toLowerCase())
    ).slice(0, 3);

    const showTestimonials = localTestimonials.length > 0
        ? localTestimonials
        : TESTIMONIALS.slice(0, 3);

    return (
        <main data-testid={`area-detail-${area.slug}`} className="pt-24">
            {/* Hero */}
            <section className="relative py-24 md:py-32 bg-ink text-bone overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-moss via-ink to-ink" />
                <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-copper/15 blur-3xl" />
                <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
                    <Link
                        to="/#area"
                        data-testid="area-back-link"
                        className="inline-flex items-center gap-2 text-sm text-bone/70 hover:text-ember mb-8 reveal"
                    >
                        <ArrowLeft size={16} /> All service areas
                    </Link>
                    <div className="text-xs tracking-[0.22em] uppercase font-bold text-ember mb-5 reveal">
                        <span className="h-px w-10 bg-ember inline-block align-middle mr-3" />
                        Service Area · {area.state} · ZIP {area.zip}
                    </div>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight leading-[0.98] reveal">
                        Insulation in <span className="text-ember">{area.city}</span>
                    </h1>
                    <p className="mt-8 text-lg md:text-xl text-bone/85 max-w-3xl leading-relaxed reveal">
                        Spray foam, attic insulation, crawl space encapsulation, air sealing, and
                        full energy audits for {area.city} homeowners — done by a BPI-certified
                        crew, with up to{" "}
                        <strong className="text-ember">{COMPANY.maxRebate}</strong> in NICOR &amp;
                        ComEd rebates filed for you.
                    </p>
                    <div className="mt-10 flex flex-col sm:flex-row gap-4 reveal">
                        <a
                            href="#quote"
                            data-testid="area-cta-quote"
                            className="btn-lift inline-flex items-center justify-center gap-2 bg-copper text-bone font-semibold py-4 px-8 hover:bg-copper-hover"
                        >
                            Free quote in {area.city} <ArrowRight size={18} />
                        </a>
                        <a
                            href={COMPANY.phoneHref}
                            data-testid="area-cta-phone"
                            className="btn-lift inline-flex items-center justify-center gap-2 border-2 border-bone text-bone font-semibold py-4 px-8 hover:bg-bone hover:text-ink"
                        >
                            <Phone size={16} /> {COMPANY.phone}
                        </a>
                    </div>
                </div>
            </section>

            {/* Local intro */}
            <section className="py-20 md:py-28 bg-bone">
                <div className="max-w-4xl mx-auto px-6 md:px-12">
                    <div className="text-xs tracking-[0.22em] uppercase font-bold text-moss mb-5 reveal">
                        Why {area.city} homes leak energy
                    </div>
                    <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-8 reveal">
                        Built between 1950 and 1990? Your insulation is almost certainly
                        underbuilt.
                    </h2>
                    <div className="space-y-5 text-ink/85 leading-relaxed text-lg reveal">
                        <p>
                            Most of the housing stock in {area.city}, {area.state} was built
                            before modern energy codes — meaning attics commonly sit at R-19 to
                            R-30 when current code calls for R-49 to R-60, and air sealing was
                            essentially nonexistent. The result: cold floors, drafty rooms, ice
                            dams in winter, and gas bills that don&apos;t match the home&apos;s
                            size.
                        </p>
                        <p>
                            We&apos;ve been working {area.city} homes for over a decade. Every
                            project starts with a thermal scan and a blower door test so we know
                            exactly what your house needs — no guesswork, no upsell. Then the
                            same crew that scoped the job does the install.
                        </p>
                    </div>
                </div>
            </section>

            {/* Services available */}
            <section className="py-20 md:py-28 bg-moss text-bone relative overflow-hidden">
                <div className="absolute inset-0 grain pointer-events-none" />
                <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
                    <div className="text-xs tracking-[0.22em] uppercase font-bold text-ember mb-5 reveal">
                        Services in {area.city}
                    </div>
                    <h2 className="font-heading text-3xl md:text-5xl font-bold leading-[1.05] tracking-tight mb-12 reveal">
                        Full-envelope insulation, all from one crew.
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-bone/10 border border-bone/15">
                        {SERVICES.slice(0, 9).map((s, idx) => (
                            <Link
                                key={s.id}
                                to={`/services/${s.id}`}
                                data-testid={`area-service-link-${s.id}`}
                                className="bg-moss p-7 hover:bg-moss-hover group transition-colors reveal"
                                style={{ transitionDelay: `${(idx % 6) * 50}ms` }}
                            >
                                <h3 className="font-heading text-xl font-bold tracking-tight mb-2 group-hover:text-ember transition-colors">
                                    {s.title}
                                </h3>
                                <p className="text-sm text-bone/75 leading-relaxed">
                                    {s.blurb}
                                </p>
                                <div className="mt-4 inline-flex items-center gap-1 text-xs uppercase tracking-wider font-bold text-ember">
                                    {s.title} in {area.city}{" "}
                                    <ArrowRight size={12} />
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Local reviews */}
            <section className="py-20 md:py-28 bg-bone">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="text-xs tracking-[0.22em] uppercase font-bold text-moss mb-5 reveal">
                        Reviews
                    </div>
                    <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-10 reveal">
                        What {area.city}-area homeowners say.
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        {showTestimonials.map((t, i) => (
                            <article
                                key={i}
                                data-testid={`area-testimonial-${i}`}
                                className="bg-white border border-line p-7 reveal"
                            >
                                <div className="flex mb-4">
                                    {Array.from({ length: t.rating }).map((_, k) => (
                                        <Star
                                            key={k}
                                            size={14}
                                            className="fill-ember text-ember"
                                        />
                                    ))}
                                </div>
                                <p className="text-sm leading-relaxed">{t.text}</p>
                                <div className="mt-5 pt-3 border-t border-line">
                                    <div className="font-heading font-bold text-sm">
                                        {t.name}
                                    </div>
                                    <div className="text-[10px] uppercase tracking-wider text-muted_ink">
                                        {t.location}
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* Other areas */}
            <section className="py-20 bg-ink text-bone">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="text-xs tracking-[0.22em] uppercase font-bold text-ember mb-5 reveal">
                        Nearby cities
                    </div>
                    <div className="flex flex-wrap gap-2 reveal">
                        {SERVICE_AREAS.filter((a) => a.slug !== area.slug).map((a) => (
                            <Link
                                key={a.slug}
                                to={`/areas/${a.slug}`}
                                data-testid={`nearby-area-${a.slug}`}
                                className="text-xs uppercase tracking-wider font-bold border border-bone/20 px-4 py-2 hover:bg-bone hover:text-ink transition-colors"
                            >
                                {a.city}, {a.state}
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            <ContactForm />
        </main>
    );
}
